import { APIError, useFormikSubmit } from "@certn/api";
import { components } from "@certn/api/generated-schemas/client-portal";
import { useClientPortalAPI } from "@certn/api/src/useClientPortalAPI";
import { showToast } from "@certn/cascadia";
import {
  BasicButton,
  Card,
  Container,
  Divider,
  PrimaryButton,
  Radio,
  RadioGroupFormikField,
  SectionMessage,
  SelectFormikField,
  SelectItem,
  Stack,
  Text,
  TextField,
  TextInputFormikField,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { Form, Formik, FormikHelpers } from "formik";
import { useCallback, useContext, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { object, string } from "yup";

import { WhatsThis } from "../../../components/WhatsThis";
import {
  AtsIntegration,
  IcimsIntegrationContext,
} from "../../IcimsIntegrationContext";
import { AtsIntegrationStepLayout } from "../AtsIntegrationStepLayout";

interface Props {
  initialValues?: components["schemas"]["AtsIntegrationDetail"];
  reload?: () => void;
  isEditing?: boolean;
}

export function IcimsSetupStep({
  initialValues,
  reload,
  isEditing,
}: Props = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [authenticationError, setAuthenticationError] = useState(false);
  const [isSystemError, setIsSystemError] = useState(false);
  const context = useContext(IcimsIntegrationContext);
  const { atsIntegrationData: contextData, setAtsIntegration } = context || {
    atsIntegrationData: null,
    setAtsIntegration: () => {},
  };

  // Determine if we're in edit mode
  const isEditMode = !!initialValues?.id;

  // Use initialValues for edit mode, context for create mode
  const integrationData = isEditMode
    ? {
        customer_id: initialValues?.icims_integration?.customer_id || "",
        icims_username: initialValues?.icims_integration?.icims_username || "",
        icims_password: initialValues?.icims_integration?.icims_password || "",
        icims_webhook_username:
          initialValues?.icims_integration?.icims_webhook_username || "",
        icims_webhook_password:
          initialValues?.icims_integration?.icims_webhook_password || "",
        report_url_type: initialValues?.report_url_type || "",
        customer_group_id:
          initialValues?.icims_integration?.customer_group_id || "",
      }
    : contextData || {
        customer_id: "",
        icims_username: "",
        icims_password: "",
        icims_webhook_username: "",
        icims_webhook_password: "",
        report_url_type: "CLIENT_PORTAL_URL",
        customer_group_id: "",
      };

  const { data: customerGroups, isLoading: isLoadingCustomerGroups } =
    useClientPortalAPI(
      "/api/internal/client-portal/ats-integrations/customer-groups/"
    );

  const validationSchema = object().shape({
    customer_id: string().required().label(t("iCIMS Customer ID")),
    icims_username: string().required().label(t("Username")),
    icims_password: string().required().label(t("Password")),
    icims_webhook_username: string().required().label(t("Webhook username")),
    icims_webhook_password: string().required().label(t("Webhook password")),
    customer_group_id: string().required().label(t("Default group")),
  });

  const { submit: createIntegration, isSubmitting: isCreating } =
    useFormikSubmit("/api/internal/client-portal/ats-integrations/create/", {
      onSuccess: () => {
        showToast(
          t("Success, {{integration}} has been successfully enabled.", {
            integration: "iCIMS",
          }),
          {
            type: "success",
          }
        );
        navigate("/settings/ats-integrations");
      },
      showToastOnError: true,
    });

  const { submit: updateIntegration, isSubmitting: isUpdating } =
    useFormikSubmit<components["schemas"]["AtsIntegrationDetail"]>(
      `/api/internal/client-portal/ats-integrations/${initialValues?.id}/update/`,
      {
        onSuccess: () => {
          showToast(t("Integration updated successfully"), {
            type: "success",
          });
          reload?.();
          navigate("/settings/ats-integrations");
        },
        showToastOnError: true,
      }
    );

  const handleSubmit = useCallback(
    async (
      values: AtsIntegration,
      formikHelpers: FormikHelpers<AtsIntegration>
    ) => {
      setAuthenticationError(false);
      setIsSystemError(false);
      if (!isEditMode) {
        setAtsIntegration(values);
      }

      try {
        if (isEditMode) {
          // Update mode - submit all values directly like other integrations
          await updateIntegration(
            {
              report_url_type: (values.report_url_type ||
                "CLIENT_PORTAL_URL") as components["schemas"]["ReportUrlTypeEnum"],
              lever_integration: null,
              workday_integration: null,
              icims_integration: {
                customer_id: values.customer_id,
                icims_username: values.icims_username,
                icims_password: values.icims_password,
                icims_webhook_username: values.icims_webhook_username,
                icims_webhook_password: values.icims_webhook_password,
                customer_group_id: values.customer_group_id || "",
              },
            } as unknown as components["schemas"]["AtsIntegrationDetail"],
            formikHelpers
          );
        } else {
          // Create mode
          await createIntegration(
            {
              report_url_type: (values.report_url_type ||
                "CLIENT_PORTAL_URL") as components["schemas"]["ReportUrlTypeEnum"],
              icims_integration: {
                customer_id: values.customer_id,
                icims_username: values.icims_username,
                icims_password: values.icims_password,
                icims_webhook_username: values.icims_webhook_username,
                icims_webhook_password: values.icims_webhook_password,
                customer_group_id: values.customer_group_id || "",
              },
            } as components["schemas"]["AtsIntegrationCreate"],
            formikHelpers
          );
        }
      } catch (error: unknown) {
        // Error handling is done in the onFailure callbacks
        // Check if it's a validation error or system error
        const status =
          error instanceof APIError
            ? error.status
            : (error as { status?: number; response?: { status?: number } })
                ?.status ||
              (error as { status?: number; response?: { status?: number } })
                ?.response?.status;
        if (status === 400 || status === 401 || status === 403) {
          setAuthenticationError(true);
          setIsSystemError(false);
        } else if (status !== undefined) {
          setIsSystemError(true);
          setAuthenticationError(false);
        }
      }
    },
    [isEditMode, setAtsIntegration, createIntegration, updateIntegration]
  );

  const isSubmitting = isCreating || isUpdating;
  const isInactive = initialValues?.is_active === false;

  return (
    <>
      <Formik
        initialValues={integrationData}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        enableReinitialize={isEditMode}
      >
        {({ handleSubmit, isValid, values, ...helpers }) => {
          const copyToClipBoard = (key: keyof AtsIntegration) => {
            navigator.clipboard.writeText(values?.[key] || "");
            showToast(t("Text copied to clipboard"), { type: "success" });
          };

          const formFields = (
            <>
              {authenticationError && (
                <SectionMessage
                  type="danger"
                  sectionTitle={t("Authentication failed")}
                  icon="error"
                >
                  <Trans>
                    We could not verify your API credentials. Please check your
                    details and try again. If you still encounter an error,
                    please contact your iCIMS administrator.
                  </Trans>
                </SectionMessage>
              )}

              {isSystemError && (
                <SectionMessage
                  type="danger"
                  sectionTitle={t("System error")}
                  icon="error"
                >
                  <Trans>
                    We are not able to verify your API credentials, as the iCIMS
                    platform is not responsive. Please try after some time. If
                    the issue persists, please contact Certn support team.
                  </Trans>
                </SectionMessage>
              )}

              <Text style="global/heading-medium">
                {t("iCIMS account authentication details")}
              </Text>

              <section>
                <TextInputFormikField
                  className={styles.textFieldSpacing}
                  label={t("iCIMS Customer ID")}
                  name="customer_id"
                  testID="customer-id"
                  isRequired
                  isDisabled={isSubmitting || isInactive}
                />
              </section>

              {[
                {
                  name: "icims_username" as keyof AtsIntegration,
                  testID: "icims-username",
                  label: t("iCIMS username"),
                },
                {
                  name: "icims_password" as keyof AtsIntegration,
                  testID: "icims-password",
                  label: t("iCIMS password"),
                },
              ].map(({ name, testID, label }) => (
                <section key={name}>
                  <TextInputFormikField
                    className={styles.textFieldSpacing}
                    name={name}
                    testID={testID}
                    label={label}
                    isRequired
                    isDisabled={isSubmitting || isInactive}
                    info={
                      <WhatsThis>
                        <div>
                          <Trans
                            i18nKey="Your {{label}} allows Certn to seamlessly integrate with your iCIMS account. You will be required to send this to iCIMS to enable your integration. Please refer to the <2>iCIMS ↔ Certn Integration guide</2>."
                            values={{ label }}
                            components={[
                              <></>,
                              <></>,
                              // eslint-disable-next-line jsx-a11y/anchor-has-content
                              <a
                                key="link"
                                href="https://help.certn.co/hc/en-us/articles/4402980872339-Certn-Icims-Integration-Setup"
                                target="_blank"
                                rel="noreferrer"
                              />,
                            ]}
                          />
                        </div>
                      </WhatsThis>
                    }
                  />
                </section>
              ))}

              {[
                {
                  name: "icims_webhook_username" as keyof AtsIntegration,
                  testID: "icims-webhook-username",
                  label: t("Webhook username"),
                },
                {
                  name: "icims_webhook_password" as keyof AtsIntegration,
                  testID: "icims-webhook-password",
                  label: t("Webhook password"),
                },
              ].map(({ name, testID, label }) => (
                <section key={name}>
                  {isEditing ? (
                    <TextField
                      className={styles.textFieldSpacing}
                      label={label}
                      name={name}
                      testID={testID}
                      isDisabled
                      isRequired
                      value={values[name]}
                    />
                  ) : (
                    <Stack orientation="horizontal" align="end" gap="space-2">
                      <TextInputFormikField
                        className={styles.textFieldSpacing}
                        name={name}
                        testID={testID}
                        label={label}
                        isDisabled
                        isRequired
                        info={
                          <WhatsThis>
                            <div>
                              <Trans
                                i18nKey="Your {{label}} allows Certn to seamlessly integrate with your iCIMS account. You will be required to send this to iCIMS to enable your integration. Please refer to the <2>iCIMS ↔ Certn Integration guide</2>."
                                values={{ label }}
                                components={[
                                  <></>,
                                  <></>,
                                  // eslint-disable-next-line jsx-a11y/anchor-has-content
                                  <a
                                    key="link"
                                    href="https://help.certn.co/hc/en-us/articles/4402980872339-Certn-Icims-Integration-Setup"
                                    target="_blank"
                                    rel="noreferrer"
                                  />,
                                ]}
                              />
                            </div>
                          </WhatsThis>
                        }
                      />
                      <BasicButton
                        onPress={() => copyToClipBoard(name)}
                        type="button"
                        size="medium"
                        testID="copy-username-button"
                        leftIcon="content_copy"
                      >
                        {t("Copy")}
                      </BasicButton>
                    </Stack>
                  )}
                </section>
              ))}

              <RadioGroupFormikField
                label={t("Report URL type")}
                testID="report-url-type"
                name="report_url_type"
                orientation="horizontal"
                isRequired
                isDisabled={isInactive}
                defaultValue="DIRECT_PDF_URL"
              >
                <Radio testID="direct-pdf-url" value="DIRECT_PDF_URL">
                  {t("PDF URL")}
                </Radio>
                <Radio testID="client-portal-url" value="CLIENT_PORTAL_URL">
                  {t("Client Portal URL (Authenticated)")}
                </Radio>
              </RadioGroupFormikField>

              <Divider />

              <section>
                <SelectFormikField
                  label={t("Select a group")}
                  testID="icims-customer-group"
                  name="customer_group_id"
                  isRequired
                  isLoading={isLoadingCustomerGroups}
                  isDisabled={isSubmitting || isInactive}
                >
                  {customerGroups?.map(({ id, name }) => (
                    <SelectItem key={id} id={id} textValue={name}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectFormikField>
              </section>
            </>
          );

          const formContent = (
            <Container
              layout="narrow"
              testID="icims-setup-page-outermost-container"
              className={styles.container}
            >
              <Form>
                {isEditMode && (
                  <Card type="large" className={styles.card}>
                    <Stack gap="space-4">
                      <div className={styles.mainContentContainer}>
                        {formFields}
                        <Stack
                          orientation="horizontal"
                          justify="end"
                          gap="space-4"
                        >
                          <BasicButton
                            testID="cancel-icims"
                            type="button"
                            isDisabled={isSubmitting}
                            onPress={() =>
                              navigate("/settings/ats-integrations")
                            }
                          >
                            {t("Cancel")}
                          </BasicButton>
                          <PrimaryButton
                            testID="save-icims"
                            type="submit"
                            isDisabled={
                              isSubmitting ||
                              !isValid ||
                              !initialValues?.is_active
                            }
                          >
                            {t("Save changes")}
                          </PrimaryButton>
                        </Stack>
                      </div>
                    </Stack>
                  </Card>
                )}

                {!isEditMode && (
                  <>
                    <Stack gap="space-2">
                      <Text
                        as="h1"
                        className={styles.titleSpacing}
                        style="global/title-small"
                      >
                        {t("iCIMS Integration")}
                      </Text>
                      <Stack gap="space-2">
                        <Text style="global/heading-medium">
                          {t(
                            "Add your integration details here to set up the connection with Certn. These enable Certn to send information to iCIMS."
                          )}
                        </Text>

                        <SectionMessage type="warning" icon="warning">
                          <Trans>
                            Make sure you save the webhook username and password
                            somewhere safe to send to iCIMS as you will not be
                            able to see it later.
                          </Trans>
                        </SectionMessage>
                      </Stack>
                    </Stack>
                    <div className={styles.mainContentContainer}>
                      {formFields}
                    </div>
                  </>
                )}
              </Form>
            </Container>
          );

          if (isEditMode) {
            return formContent;
          }

          return (
            <AtsIntegrationStepLayout
              onComplete={handleSubmit}
              disableCompletion={!isValid}
              submitting={isSubmitting}
            >
              {formContent}
            </AtsIntegrationStepLayout>
          );
        }}
      </Formik>
    </>
  );
}

const styles = {
  titleSpacing: css({ marginBottom: "var(--space-1)" }),
  mainContentContainer: css({
    display: "flex",
    gap: "var(--space-5)",
    flexDirection: "column",
    marginTop: "var(--space-4)",
  }),
  container: css({
    rowGap: "1rem !important",
  }),
  card: css({
    marginTop: "var(--space-4)",
    padding: "var(--space-4)",
    gap: "var(--space-4)",
  }),
  textFieldSpacing: css({ display: "flex", flex: "1" }),
};
